import { newSpecPage, SpecPage } from "@stencil/core/testing";
import { Button } from "./button";

global.MutationObserver = class {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    constructor(callback) {}
    disconnect() {}
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    observe(element, initObject) {}
} as typeof MutationObserver;

describe("UxButton Component", () => {
    describe("property management", () => {
        it("should apply all props to native button", async () => {
            const page = await newSpecPage({
                components: [Button],
                html: `<ux-button type="submit" label="someLabel" name="someTestName" disabled></ux-button>`
            });
            const shadowButton = getButtonElement(page);
            expect(shadowButton.getAttribute("disabled")).not.toBeNull();
            expect(shadowButton.getAttribute("type")).toEqual("submit");
            expect(shadowButton.getAttribute("name")).toEqual("someTestName");
            expect(shadowButton.getAttribute("aria-label")).toEqual("someLabel");
        });
        it("should apply all props to native anchor", async () => {
            const page = await newSpecPage({
                components: [Button],
                html: `<ux-button label="someLabel" name="someTestName" href="fo" target="_self" download></ux-button>`
            });
            const shadowAnchor = getAnchorElement(page);
            expect(shadowAnchor.getAttribute("aria-label")).toEqual("someLabel");
            expect(shadowAnchor.getAttribute("href")).toEqual("fo");
            expect(shadowAnchor.getAttribute("target")).toEqual("_self");
            expect(shadowAnchor.getAttribute("download")).not.toBeNull();
        });
    });
    describe("as button", () => {
        it("should render my component and set default values", async () => {
            const page = await newSpecPage({
                components: [Button],
                html: `<ux-button></ux-button>`
            });
            expect(page.root).toEqualHtml(`
               <ux-button iconplacement="start" size="default" variant="default">
                  <mock:shadow-root>
                    <button tabindex="0">
                      <span class="ux-button__icon--none">
                        <slot name="icon"></slot>
                      </span>
                      <span class="ux-button__label ux-button__label--truncate">
                        <slot></slot>
                      </span>
                    </button>
                  </mock:shadow-root>
                </ux-button>
            `);
        });

        it("should render with label prop", async () => {
            const testLabel = "SomeLabel";
            const page = await newSpecPage({
                components: [Button],
                html: `<ux-button label="${testLabel}"></ux-button>`
            });
            const button = page.body.querySelector("ux-button");
            const label = button.shadowRoot.querySelector(".ux-button__label").textContent;
            expect(label).toEqual(testLabel);
        });

        it("should render with hidden label", async () => {
            const testLabel = "SomeLabel";
            const page = await newSpecPage({
                components: [Button],
                html: `<ux-button label="${testLabel}" size="icon"></ux-button>`
            });
            const button = page.body.querySelector("ux-button");
            const label = button.shadowRoot.querySelector(".ux-button__label--hidden");
            expect(label).toBeTruthy();
        });

        it("should render with icon", async () => {
            const testLabel = "SomeLabel";
            const page = await newSpecPage({
                components: [Button],
                html: `<ux-button label="${testLabel}" icon="someIcon"></ux-button>`
            });
            const button = page.body.querySelector("ux-button");
            const icon = button.shadowRoot.querySelector(".ux-button__icon");
            expect(icon).toBeTruthy();
        });

        it("should render without icon", async () => {
            const testLabel = "SomeLabel";
            const page = await newSpecPage({
                components: [Button],
                html: `<ux-button label="${testLabel}"></ux-button>`
            });
            const button = page.body.querySelector("ux-button");
            const icon = button.shadowRoot.querySelector(".ux-button__icon--none");
            expect(icon).toBeTruthy();
        });

        it("should render disabled", async () => {
            const page = await newSpecPage({
                components: [Button],
                html: `<ux-button label="someLabel" disabled name="someTestName"></ux-button>`
            });
            const shadowButton = getButtonElement(page);
            expect(shadowButton.getAttribute("disabled")).not.toBeNull();
            expect(shadowButton.getAttribute("tabIndex")).toEqual("-1");
        });
    });
    describe("as link", () => {
        it("should render my component and set default values", async () => {
            const page = await newSpecPage({
                components: [Button],
                html: `<ux-button href="someHref"></ux-button>`
            });
            expect(page.root).toEqualHtml(`
               <ux-button href="someHref" iconplacement="start" size="default" variant="default">
                  <mock:shadow-root>
                    <a href="someHref" part="button" tabindex="0" target="_self">
                      <span aria-hidden="true" class="ux-button__icon--none">
                        <slot name="icon"></slot>
                      </span>
                      <span class="ux-button__label ux-button__label--truncate">
                        <slot></slot>
                      </span>
                    </a>
                  </mock:shadow-root>
                </ux-button>
            `);
        });

        it("should render without href if disabled", async () => {
            const page = await newSpecPage({
                components: [Button],
                html: `<ux-button label="someLabel" disabled name="someTestName" href="fo" target="_self" download></ux-button>`
            });
            const shadowAnchor = getAnchorElement(page);
            expect(shadowAnchor.getAttribute("href")).toBeNull();
            expect(shadowAnchor.getAttribute("aria-disabled")).toBeTruthy();
            expect(shadowAnchor.getAttribute("tabIndex")).toEqual("-1");
        });

        it("should render icon", async () => {
            const testLabel = "SomeLabel";
            const page = await newSpecPage({
                components: [Button],
                html: `<ux-button label="${testLabel}" icon="someIcon"></ux-button>`
            });
            const button = page.body.querySelector("ux-button");
            const icon = button.shadowRoot.querySelector(".ux-button__icon");
            expect(icon).toBeTruthy();
        });

        it("should render without icon", async () => {
            const testLabel = "SomeLabel";
            const page = await newSpecPage({
                components: [Button],
                html: `<ux-button label="${testLabel}"></ux-button>`
            });
            const button = page.body.querySelector("ux-button");
            const icon = button.shadowRoot.querySelector(".ux-button__icon--none");
            expect(icon).toBeTruthy();
        });
    });
});

const getButtonElement = (page: SpecPage) => {
    const button = page.body.ownerDocument.body.querySelector("ux-button");
    return button.shadowRoot.querySelector("button");
};

const getAnchorElement = (page: SpecPage) => {
    const button = page.body.ownerDocument.body.querySelector("ux-button");
    return button.shadowRoot.querySelector("a");
};
